import React, { useState, useEffect, useCallback, createContext, useRef, useImperativeHandle, forwardRef, useMemo } from 'react'
import { Form, Spin } from 'antd'
import { OneButton as Button, OneModal as Modal, OneMessage as SLMessage } from '@yy/one-ui'
import { ArrowLeft } from '@yy/one-icon'
import { useHistory } from 'react-router-dom'
import type { FormProps } from 'antd/es/form'
import classNames from 'classnames'
import styles from './index.module.less'
import { useTranslation } from 'react-i18next'
import type { FormInstance } from 'antd/es/form'
import { setShouldRefreshCache } from '@/store/cacheSlice'
import { useDispatch } from '@/store'
import { useBoolean } from 'ahooks'
import TopUpdateBar from './TopUpdateBar'
import { Helmet } from 'react-helmet'

export const FormContext = createContext<FormInstance<any | null>>(null)
export enum STATUS {
  FORM_LAYOUT_NULL,
  FORM_LAYOUT_EDITING,
}
interface Iprops<V = Record<string, any>> extends Omit<FormProps, 'title'> {
  title?: string | React.ReactNode
  subTitle?: string
  /** 页面标题 */
  helmetTitle?: string
  pageType?: 'first' | 'secondary'
  /* 是否需要返回按钮 */
  needBack?: boolean
  /** 自定义头部 */
  customizeHeader?: React.ReactNode
  /** 脚本*/
  customFooter?: React.ReactNode

  /** 提交表单的回调事件 */
  onFinish?: (value: V) => Promise<void>
  /** 提交的回调事件 */
  onSuccess?: () => void
  children: React.ReactNode
  /** 页面是内滚动 */
  scrollInside?: boolean
  className?: string
  editType?: 'add' | 'edit'
  /** 当前表单的状态 */
  currentStatus?: STATUS
  /**页面id */
  id?: string
  rootClass?: string
  saveText?: string
  /**页面loading */
  isSpining?: boolean
  /**finish时候是否对error情况进行提示 */
  errCodeMessage?: Record<string, string>
  /** 指定修改特定的表单元素后，离开当前页面不弹出确认提示。值为表单元素的 name 值 */
  excludeConfirmKeys?: string[]
  /** 没有上层页面, 保存或者取消操作之后，不返回，停留在当前页面  */
  successCurrentPage?: boolean
  goBack?: () => void
  /** 重置数据 */
  reloadData?: () => void
  /** 是否固定宽度 宽度为1200px */
  fixWidth?: boolean
  /** 是否顶部展示保存按钮 */
  saveButtonOnTop?: boolean
  /** 隐藏顶部保存条 */
  hideTopUpdateBar?: boolean
  whenErrorSubmitDisabled?: boolean
  submitBtnProps?: React.ComponentProps<typeof Button>
  /**没有上层页面的情况下默认跳转的url */
  defaultBackUrl?: string
  /** 更新按钮文案 */
  updateText?: string
  /**
   * 是否有变更
   * @description
   * 因为部分并没有 formItem 覆盖
   * 这里外部直接告诉你有没有变更
   * 这样能减少一层表单数据
   */
  hadChanged?: boolean
  /** 放弃更改回调 */
  onReset?: () => void
}

const PageContainer = <V,>(props: Iprops, ref) => {
  const {
    className,
    title,
    subTitle,
    needBack,
    children,
    form,
    initialValues,
    onFinish,
    onFinishFailed,
    onSuccess,
    customizeHeader,
    scrollInside,
    layout = 'horizontal',
    pageType,
    editType = 'add',
    customFooter,
    isSpining = false,
    id,
    rootClass,
    saveText,
    errCodeMessage = [],
    // excludeConfirmKeys = [],
    /** title是组件则需要传页面标题进来 */
    helmetTitle,
    successCurrentPage = false,
    goBack: onGoBack,
    reloadData,
    fixWidth = false,
    saveButtonOnTop,
    preserve,
    hideTopUpdateBar = false,
    whenErrorSubmitDisabled,
    submitBtnProps,
    defaultBackUrl,
    updateText,
    hadChanged = false,
    onReset,
  } = props

  const history = useHistory()
  // const { pathname } = useLocation()
  const [loading, setLoading] = useState<boolean>(false)
  const [spining, setSpining] = useState<boolean>(isSpining)
  const [hadChange, { toggle: setHadChange }] = useBoolean(false)

  const hadChangeRef = useRef<boolean>()

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const sleep = (time) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(null)
      }, time)
    })
  }
  const waitSpining = useCallback(async () => {
    if (editType === 'add') {
      setSpining(true)
      await sleep(2000)
      setSpining(false)
    }
  }, [editType])

  /**
   * 回填表单的值
   */
  useEffect(() => {
    form?.setFieldsValue(initialValues)
  }, [initialValues, form])

  // 支持外部控制loading
  useEffect(() => {
    setSpining(isSpining)
  }, [isSpining])

  const goBack = useCallback(() => {
    // 如果没有上层页面，例如销售设置，保存或者取消操作之后，不返回，停留在当前页面。
    if (successCurrentPage) return
    // 如果页面栈只有一个并且有默认返回地址，则跳转到默认地址
    if (history?.length <= 1 && defaultBackUrl) {
      return history.push(defaultBackUrl)
    }

    history.goBack()
  }, [defaultBackUrl, history, successCurrentPage])
  /**
   * 表单未保存离开确定
   */
  const resetConfirm = useCallback(
    (backUrl?: string) => {
      Modal.danger({
        title: t('user.leave.confirm'),
        okText: t('user.leave.ok'),
        cancelText: t('user.leave.cancel'),
        onOk() {
          setHadChange(false)
          // 返回上一级会命中路由监听，确保路由监听拿到的hadChange是false
          hadChangeRef.current = false
          form?.resetFields()
          dispatch(setShouldRefreshCache(false))
          onReset && onReset()
          if (backUrl) {
            history.push(backUrl)
          } else {
            // 没有上一父级页面的，放弃更改后重新渲染初始化数据-产品逻辑
            if (successCurrentPage) {
              reloadData && reloadData()
              return
            }
            goBack()
          }
        },
      })
    },
    [t, setHadChange, form, dispatch, onReset, history, successCurrentPage, goBack, reloadData]
  )
  /**
   * 表单提交
   */
  const handleFinish = useCallback(
    (value: V) => {
      setLoading(true)

      onFinish?.(value)
        .then(async () => {
          setHadChange(false)
          hadChangeRef.current = false
          onSuccess?.()
          // 等待2秒之后再返回
          await waitSpining()
          goBack()
          dispatch(setShouldRefreshCache(true))
        })
        .catch((err) => {
          let msg = typeof err === 'string' ? err : err.message
          if (Object.prototype.toString.call(errCodeMessage) === '[object Object]') {
            const errCode = Object.keys(errCodeMessage)?.find((code) => code === err.code)
            if (errCode) {
              msg = errCodeMessage[errCode]
            }
          }
          // todo: 走这个逻辑的时候，会提示2次，后续可以优化1下
          msg && SLMessage.error(msg)
          // eslint-disable-next-line no-console
          console.error(err)
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [dispatch, errCodeMessage, goBack, onFinish, onSuccess, setHadChange, waitSpining]
  )

  /** 获取表单变更 */
  const handleValuesChange = useCallback(
    (changeValues, allValues) => {
      if (!hadChange || !hadChangeRef.current) {
        setHadChange(true)
        hadChangeRef.current = true
      }
      if (props.onValuesChange) {
        props.onValuesChange(changeValues, allValues)
        return
      }
    },
    [props, hadChange, setHadChange]
  )

  /**
   * 页面刷新回调
   */
  const beforeunloadCallback = useCallback(
    (e: BeforeUnloadEvent) => {
      if (hadChanged || (form && hadChangeRef.current)) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    [hadChanged, form]
  )
  /**
   * 监听刷新
   */
  useEffect(() => {
    window.addEventListener('beforeunload', beforeunloadCallback)
    return () => {
      window.removeEventListener('beforeunload', beforeunloadCallback)
    }
  }, [beforeunloadCallback])

  /**
   * 监听切换路由
   */
  useEffect(() => {
    const unblock = history.block((location) => {
      if (form && hadChangeRef.current && !hideTopUpdateBar) {
        resetConfirm(location.pathname + location.search)
        return false
      }
    })
    return () => {
      unblock()
    }
  }, [history, form, resetConfirm, hideTopUpdateBar])

  const SubmitButton = useMemo(
    () => (
      <Button className={styles.btnSubmit} key="2" type="primary" loading={loading} onClick={form?.submit} {...submitBtnProps}>
        {saveText || t('common.save')}
      </Button>
    ),
    [form?.submit, loading, saveText, submitBtnProps, t]
  )

  // title
  const titleHeader = useMemo(() => {
    return title ? (
      <div className={styles.head}>
        <div className={styles.breadcrumb}>
          {needBack ? (
            <div className={styles.backBtn} onClick={onGoBack ?? goBack}>
              <ArrowLeft
                style={{
                  fontSize: 20,
                }}
              />
            </div>
          ) : null}
          <div className={styles.title}>
            {title}
            {subTitle ? <div className={styles.subTitle}>{subTitle}</div> : null}
          </div>
        </div>
        <div className={styles.buttonBar}>{saveButtonOnTop ? SubmitButton : customizeHeader}</div>
      </div>
    ) : null
  }, [SubmitButton, customizeHeader, goBack, needBack, onGoBack, saveButtonOnTop, title, subTitle])

  useImperativeHandle(ref, () => ({ handleFinish }))

  const isSecondPage = useMemo(() => {
    if (pageType === 'first') return false
    return needBack
  }, [needBack, pageType])

  const content = useMemo(() => {
    return (
      <div id={id} className={classNames(styles.root, fixWidth && styles.fixWidth, rootClass)}>
        <Helmet>
          <title>{helmetTitle || title}</title>
        </Helmet>
        <Spin spinning={spining}>
          {/* 全局统一保存、取消处理 */}
          {hadChanged || (form && hadChange && !hideTopUpdateBar) ? (
            <TopUpdateBar
              okConfirm={form.submit}
              cancelConfirm={() => resetConfirm()}
              whenErrorSubmitDisabled={whenErrorSubmitDisabled}
              updateText={updateText}
              submitBtnProps={submitBtnProps}
            />
          ) : null}
          <div className={classNames({ [styles.secondWrap]: isSecondPage, [styles.indexWrap]: !isSecondPage }, className)}>
            {titleHeader}

            <div className={classNames({ [styles.content]: true, [styles.noScroll]: scrollInside })}>
              {children}

              {!form ? (
                <></>
              ) : customFooter ? (
                customFooter
              ) : (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', paddingBottom: '70px' }}>{SubmitButton}</div>
              )}
            </div>
          </div>
        </Spin>
      </div>
    )
  }, [
    hadChanged,
    SubmitButton,
    children,
    className,
    customFooter,
    fixWidth,
    form,
    hadChange,
    helmetTitle,
    hideTopUpdateBar,
    id,
    isSecondPage,
    resetConfirm,
    rootClass,
    scrollInside,
    spining,
    submitBtnProps,
    title,
    titleHeader,
    updateText,
    whenErrorSubmitDisabled,
  ])

  return (
    <>
      {!form ? (
        <>{content}</>
      ) : (
        <FormContext.Provider value={form}>
          <Form
            scrollToFirstError={true}
            layout={layout}
            form={form}
            initialValues={initialValues}
            onFinish={handleFinish}
            onFinishFailed={onFinishFailed}
            onValuesChange={handleValuesChange}
            preserve={preserve}
          >
            {content}
          </Form>
        </FormContext.Provider>
      )}
    </>
  )
}

export default forwardRef(PageContainer)
